import { PlatformType } from '@cibli/shared';
import { useCallback, useEffect, useMemo } from 'react';

export const LOCAL_STORAGE_FASTPASS_LANGUAGE = 'fastpass-language';

/**
 * When the user changes language on Android, the app restarts natively so the
 * keyboard uses the new locale. On reload, we restore the chosen language from
 * localStorage and navigate to /onboarding so they don't have to go through
 * Home and language selection again.
 */
const useFastpassLanguage = (
  platformType: PlatformType | null,
  callback: (language: string) => void,
) => {
  const canFastpassLanguage = useMemo(() => {
    //console.log('useFastpassLanguage canFastpassLanguage', platformType);
    return (
      platformType &&
      [PlatformType.Booth, PlatformType.Dedicated, PlatformType.Mobile].includes(platformType)
    );
  }, [platformType]);

  /*
  useEffect(() => {
    console.log('useFastpassLanguage useEffect canFastpassLanguage', canFastpassLanguage);
  }, [canFastpassLanguage]);
  */

  const readFastpassLanguage = useCallback(() => {
    const language = localStorage.getItem(LOCAL_STORAGE_FASTPASS_LANGUAGE);
    if (language) {
      return language;
    }
    return undefined; // Maybe null was the issue looping for nothing ?
  }, []);

  const clearFastpassLanguage = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_FASTPASS_LANGUAGE);
  }, []);

  const writeFastpassLanguage = useCallback(
    (newLanguage: string) => {
      if (canFastpassLanguage) {
        localStorage.setItem(LOCAL_STORAGE_FASTPASS_LANGUAGE, newLanguage);
      }
    },
    [canFastpassLanguage],
  );

  useEffect(() => {
    if (canFastpassLanguage) {
      const language = readFastpassLanguage();
      if (language) {
        clearFastpassLanguage();
        callback(language);
      }
    }
  }, [canFastpassLanguage, callback, clearFastpassLanguage, readFastpassLanguage]);

  return { writeFastpassLanguage, clearFastpassLanguage };
};

export { useFastpassLanguage };
